import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"

// Manages the blackout-date chips: renders existing dates as removable chips,
// accepts new dates from the date input, and writes a hidden input per chip
// so the values submit as `calendar_config[blackout_dates][]`.
//
// A11Y-1 / BUTTON-4 (#13355): Add Date is disabled until a date is picked, and
// every disabled frame says so — the reason as the button's own gl-tooltip plus
// a "<label> — <reason>" accessible name (lib/gate_reason) — withdrawn the
// moment a date enables it and re-applied whenever the field empties again
// (after an add, a rejected duplicate, or a cleared field).
export default class extends Controller {
  static targets = ["chips", "input", "addButton"]
  // The reason is authored ONCE, in user_settings/_calendar_config_cards, which
  // paints it server-side on the disabled button and stamps it here as a value,
  // so the first paint and every later change say the same thing. The default
  // only serves a DOM that stamps nothing (the Jest fixtures).
  static values = {
    existing: Array,
    addReason: { type: String, default: "Pick a date first" }
  }

  connect() {
    const seed = Array.isArray(this.existingValue) ? this.existingValue : []
    seed.sort().forEach((date) => this._renderChip(date))
    this._syncAddButton()
  }

  // input -> blackout-dates#syncAddButton — nothing to add until a date is picked.
  syncAddButton() {
    this._syncAddButton()
  }

  addFromInput(event) {
    if (event) event.preventDefault?.()
    const value = (this.inputTarget.value || "").trim()
    if (!value) return

    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return

    if (this._existingDates().includes(value)) {
      this.inputTarget.value = ""
      this._syncAddButton()
      this._flashChip(value)
      return
    }

    this._renderChip(value)
    this._sortChips()
    this.inputTarget.value = ""
    this._syncAddButton()
    this._notifyChanged()
  }

  remove(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest(".gl-blackout-chip")
    if (chip) {
      chip.remove()
      this._notifyChanged()
    }
  }

  _existingDates() {
    return Array.from(this.chipsTarget.querySelectorAll("input[type=hidden]")).map((i) => i.value)
  }

  // Enabled only while the field holds a date; disabled frames carry the reason.
  _syncAddButton() {
    if (!this.hasAddButtonTarget) return
    const ready = (this.inputTarget.value || "").trim() !== ""
    syncGateReason(this.addButtonTarget, { ready, reason: this.addReasonValue })
  }

  // Chips are added/removed via button clicks, which produce no input/change
  // event — notify the surrounding form so card-dirty re-checks its baseline.
  _notifyChanged() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _renderChip(date) {
    const chip = document.createElement("span")
    chip.className = "gl-blackout-chip"
    chip.dataset.date = date

    const label = document.createElement("span")
    label.className = "gl-blackout-chip-label"
    label.textContent = this._formatDate(date)

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = "calendar_config[blackout_dates][]"
    hidden.value = date

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "gl-blackout-chip-remove"
    remove.setAttribute("aria-label", `Remove ${date}`)
    remove.innerHTML = '<i class="ph ph-x"></i>'
    remove.dataset.action = "blackout-dates#remove"

    chip.append(label, hidden, remove)
    this.chipsTarget.appendChild(chip)
  }

  _sortChips() {
    const sorted = Array.from(this.chipsTarget.children).sort((a, b) => a.dataset.date.localeCompare(b.dataset.date))
    sorted.forEach((c) => this.chipsTarget.appendChild(c))
  }

  _flashChip(date) {
    const chip = this.chipsTarget.querySelector(`.gl-blackout-chip[data-date="${date}"]`)
    if (!chip) return
    chip.classList.add("is-flashing")
    setTimeout(() => chip.classList.remove("is-flashing"), 600)
  }

  _formatDate(iso) {
    // 2026-12-25 → "Dec 25, 2026" — interpret as local date to avoid TZ shift
    const [y, m, d] = iso.split("-").map(Number)
    const dt = new Date(y, m - 1, d)
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  }
}
